import { uniqueId } from 'lodash'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../../states/containers/store'
import { ResponseMonthlyTeamReport, ResponseMonthlyMemberReport } from '../../../../../states/types/report'
import Loading from '../../../common/Loading'
import Deviation from '../Deviation'
import DeviationHeader from '../DeviationHeader'
import MonthlyBarGraph from './MonthlyBarGraph'
import MonthlyLegacyGraph from './MonthlyLegacyGraph'

interface Heatmap {
  type: string
  range: string
  target: string
  id: number
  title: string
  legend: string
  startColor: string
  endColor: string
  minimum: number
  displaySearchLink: boolean
  name: string
}

type ScoreColumnName = 'process_score' | 'resource_score' | 'improvement_score' | 'legacy_score' | 'stress_score'

interface Scores {
  label: ScoreColumnName
  current: number
  past: number | null
  company: number
  qasee?: number
  team?: number
  occupation?: number
  distributions: {
    from: number
    to: number
    percent: number
  }[]
}

interface TipDetail {
  display: boolean
  count?: number
  time?: number
  link: string
}

interface Props {
  target: string
  heatmapModalOpenCallback: (heatmap: Heatmap) => void,
}

const MonthlyLegacy: FC<Props> = (props) => {
  const {
    target,
    heatmapModalOpenCallback,
  } = props

  const {
    report,
  } = useSelector((state: RootState) => (state))

  const { t } = useTranslation()

  const subContentsRef = useRef<HTMLDivElement>(null)
  const subContentsLastRef = useRef<HTMLDivElement>(null)

  const [ruledLineHeight, setRuledLineHeight] = useState<number>(0)

  const [data, setData] = useState<ResponseMonthlyTeamReport | ResponseMonthlyMemberReport>()
  const [scores, setScores] = useState<Scores>()
  const [totalMemberNum, setTotalMemberNum] = useState<number>(0)
  const [color, setColor] = useState<string>()
  const [workStyleId, setWorkStyleId] = useState<number>(0)
  const [occupationId, setOccupationId] = useState<number>(0)

  const maxValue = useMemo(() => {
    if (!data?.modern_app_usages) return 0
    return Math.max(...data.modern_app_usages.map((value) => (
      value.use_time + 3600
    )) || 0)
  }, [data?.modern_app_usages])

  const targetIndicatorValues = (indicatorName: string) => ({
    current: data?.indicators?.find((value) => value.label === indicatorName)?.current ?? null,
    prev: data?.indicators?.find((value) => value.label === indicatorName)?.past ?? null,
    team: data?.indicators?.find((value) => value.label === indicatorName)?.team ?? null,
    company: data?.indicators?.find((value) => value.label === indicatorName)?.company ?? null,
    occupation: data?.indicators?.find((value) => value.label === indicatorName)?.occupation ?? null,
    qasee: data?.indicators?.find((value) => value.label === indicatorName)?.qasee ?? null,
    checked: data?.indicators?.find((value) => value.label === indicatorName)?.checked || false,
    appropriate_from: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_from ?? null,
    appropriate_to: data?.indicators?.find((value) => value.label === indicatorName)?.appropriate_to ?? null,
  })

  useEffect(() => {
    if (!subContentsRef.current?.offsetHeight || !subContentsLastRef.current?.offsetHeight) return
    setRuledLineHeight(subContentsRef.current?.offsetHeight - (subContentsLastRef.current?.offsetHeight / 2) + 40)
  }, [subContentsRef.current?.offsetHeight, subContentsLastRef.current?.offsetHeight])

  useEffect(() => {
    if (!target) return
    if (report.isLoading) return
    if (target === "team") {
      if (!report.monthlyTeamReport) return
      setData(report.monthlyTeamReport)
      setScores(report.monthlyTeamReport.scores?.find((score) => score.label === "legacy_score"))
      setTotalMemberNum(report.monthlyTeamReport.team?.members.length || 0)
      setColor("")
      setWorkStyleId(0)
      setOccupationId(0)
    } else {
      if (!report.monthlyMemberReport) return
      setData(report.monthlyMemberReport)
      setScores(report.monthlyMemberReport.scores?.find((score) => score.label === "legacy_score"))
      setTotalMemberNum(report.monthlyMemberReport.member?.team_member_count || 0)
      setColor(report.monthlyMemberReport.member?.applied_occupation?.icon_color)
      setWorkStyleId(0)
      setOccupationId(0)
    }
  }, [target, report])

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <>
            <section id="monthly_legacy" className="page_contents">
              <div
                className="vertical_ruler"
                style={{
                  height: `${ruledLineHeight}px`,
                }}
              />
              <div id="legacy" className="content-block monthly">
                <div className="block-detail score">
                  <div className="header_area">
                    <div className="prefix">
                      パフォーマンス
                      <br />
                      【効率度】
                    </div>
                    <div className="title">
                      デジタル化指数
                    </div>
                    <div className="point">
                      {scores?.current || 0}
                    </div>
                    <div className="description">
                      モダンなアプリケーションの利用状況やアナログな業務量からデジタル化に対する評価を行なっています。
                    </div>
                  </div>
                  <MonthlyBarGraph
                    target={target}
                    value={{
                      current: scores?.current || 0,
                      past: scores?.past || 0,
                      team: scores?.team,
                      occupation: scores?.occupation,
                      company: scores?.company || 0,
                      qasee: scores?.qasee,
                      distributions: scores?.distributions || [],
                    }}
                  />
                </div>
              </div>
            </section>
            <section ref={subContentsRef}>
              <section className="inner_contents">
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        モダンアプリ活用状況
                      </div>
                      <div className="description">
                        モダンなアプリケーションの活用状況をグラフ表示しています。
                      </div>
                    </div>
                    <div className="block-detail modern_apps_utilization_wrap">
                      <MonthlyLegacyGraph
                        target={target}
                        type="modern_app"
                        maxValue={maxValue}
                        apps={data?.modern_app_usages || []}
                        totalMemberNum={totalMemberNum}
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                      />
                    </div>
                  </div>
                </section>
              </section>
              <section
                className="inner_contents"
              >
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        モダンアプリ導入状況
                      </div>
                      <div className="description">
                        モダンなアプリケーションの導入状況を表示しています。
                      </div>
                    </div>
                    <div className="block-detail modern_app_introduction_wrap">
                      <ul className=" modern_app_introduction">
                        {
                          data?.collaborations && data.collaborations.map((collaboration) => (
                            collaboration.services?.length ? (
                              <React.Fragment key={uniqueId()}>
                                <li className="group">
                                  <div className="group_title">
                                    {collaboration.tag_name}
                                  </div>
                                  <div className="group_modern_app_wrap">
                                    {
                                      collaboration.services?.map((service) => (
                                        <React.Fragment key={uniqueId()}>
                                          <div
                                            className="group_modern_app"
                                            key={`legacy_app_${service.service_name}`}
                                          >
                                            <div
                                              className="group_modern_app_icon_wrap"
                                              style={{ background: service.use_member_count ? service.color : "#ccc" }}
                                            >
                                              <i
                                                className={`${service.icon} group_modern_app_icon`}
                                              />
                                            </div>
                                            <div className="group_modern_app_name">
                                              {service.service_name}
                                            </div>
                                            <div className="group_modern_app_tip">
                                              {service.service_name}
                                            </div>
                                          </div>
                                        </React.Fragment>
                                      ))
                                    }
                                  </div>
                                </li>
                              </React.Fragment>
                            ) : (
                              <React.Fragment key={uniqueId()} />
                            )
                          ))
                        }
                      </ul>
                    </div>
                  </div>
                </section>
              </section>
              <section
                className="inner_contents"
                ref={subContentsLastRef}
              >
                <div className="horizontal_ruler" />
                <section className="page_contents">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        アナログ
                      </div>
                    </div>
                    <div className="block-detail">
                      <DeviationHeader
                        target={target}
                        range="monthly"
                        work_style_id={workStyleId}
                        occupation_id={occupationId}
                      />
                      <Deviation
                        target={target}
                        title="print_page_count"
                        display_title="印刷枚数"
                        type="page"
                        values={targetIndicatorValues("print_page_count")}
                        labels={[0, 3, 6, 9, 12, 15, 18]}
                        appropriate={33.3}
                        appropriate_position={-50}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="inactive_count"
                        display_title="離席回数"
                        type="count"
                        values={targetIndicatorValues("inactive_count")}
                        labels={[0, 200, 400, 600, 800, 1000, 1200, 1400, 1600]}
                        appropriate={37.5}
                        appropriate_position={-37.5}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        help_text={t(`report.indicator_help.monthly.${target}.legacy.inactive_count`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="inactive_time"
                        display_title="操作なし時間"
                        type="hour"
                        values={targetIndicatorValues("inactive_time")}
                        labels={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                        appropriate={40}
                        appropriate_position={-20}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        displaySearchLink={false}
                        help_text={t(`report.indicator_help.monthly.${target}.legacy.inactive_time`)}
                        color={color}
                      />
                      <Deviation
                        target={target}
                        title="use_app_count"
                        display_title="利用アプリ"
                        type="number"
                        values={targetIndicatorValues("use_app_count")}
                        labels={[0, 10, 20, 30, 40, 50, 60, 70, 80]}
                        appropriate={50}
                        appropriate_position={-25}
                        upper_limit
                        heatmapModalOpenCallback={(heatmap) => heatmapModalOpenCallback(heatmap)}
                        button={false}
                        displaySearchLink
                        color={color}
                      />
                    </div>
                  </div>
                </section>
              </section>
            </section>
          </>
        )
      }
    </>
  )
}

export default MonthlyLegacy
